// Data page — shared type surface, domain config, and mock dataset.
//
// Mirrors the real app's canonical domain mapping in web/src/lib/data-domains.ts
// and the record shapes served by convex dataRecords:listPage.
// All data here is hardcoded mock data. "Now" is anchored to 2026-06-03.

export type DomainKey = 'email' | 'drive' | 'confluence' | 'jira' | 'calendar'

export interface DataDomain {
  key: DomainKey
  label: string
  /** Convex any_zenbot_records.model value this tab queries (real mapping). */
  model: string
  color: string
  /** Vector search (semantic) enabled — Confluence + Drive have embeddings. */
  semantic: boolean
  /** Mock total record count for pagination display. */
  total: number
  /** Sync pipeline stalled (mirrors real stale-sync surfacing). */
  stale?: boolean
}

export const DATA_DOMAINS: DataDomain[] = [
  { key: 'email', label: 'Email', model: 'GmailEmail', color: '#ea4335', semantic: false, total: 8214, stale: true },
  { key: 'drive', label: 'Drive', model: 'GoogleDriveFile', color: '#34a853', semantic: true, total: 1452 },
  { key: 'confluence', label: 'Confluence', model: 'ConfluencePage', color: '#2684ff', semantic: true, total: 1893, stale: true },
  { key: 'jira', label: 'Jira', model: 'Issue', color: '#4a9a9a', semantic: false, total: 638 },
  { key: 'calendar', label: 'Calendar', model: 'CalendarEvent', color: '#a855f7', semantic: false, total: 2651 },
]

// Columns ----------------------------------------------------------

export interface ColumnDef {
  key: string
  label: string
  /** Primary column gets title styling + most width. */
  primary?: boolean
  /** Render value as a coloured pill. */
  badge?: boolean
  /** Fixed-ish width hint (tailwind class). */
  width?: string
}

/**
 * Domain-specific columns. Common columns (synced + source link) are appended
 * by the table itself on every tab.
 */
export const DOMAIN_COLUMNS: Record<DomainKey, ColumnDef[]> = {
  email: [
    { key: 'title', label: 'Subject', primary: true },
    { key: 'sender', label: 'Sender', width: 'w-[180px]' },
    { key: 'date', label: 'Date', width: 'w-[110px]' },
  ],
  drive: [
    { key: 'title', label: 'Name', primary: true },
    { key: 'type', label: 'Type', badge: true, width: 'w-[130px]' },
    { key: 'size', label: 'Size', width: 'w-[90px]' },
    { key: 'date', label: 'Modified', width: 'w-[110px]' },
  ],
  confluence: [
    { key: 'title', label: 'Title', primary: true },
    { key: 'space', label: 'Space', badge: true, width: 'w-[130px]' },
    { key: 'status', label: 'Status', width: 'w-[100px]' },
    { key: 'date', label: 'Last edited', width: 'w-[110px]' },
  ],
  jira: [
    { key: 'key', label: 'Key', width: 'w-[90px]' },
    { key: 'title', label: 'Summary', primary: true },
    { key: 'type', label: 'Type', badge: true, width: 'w-[90px]' },
    { key: 'status', label: 'Status', badge: true, width: 'w-[110px]' },
    { key: 'priority', label: 'Priority', width: 'w-[90px]' },
    { key: 'assignee', label: 'Assignee', width: 'w-[110px]' },
    { key: 'date', label: 'Updated', width: 'w-[100px]' },
  ],
  calendar: [
    { key: 'title', label: 'Event', primary: true },
    { key: 'date', label: 'Start', width: 'w-[150px]' },
    { key: 'location', label: 'Location', width: 'w-[140px]' },
    { key: 'attendees', label: 'Attendees', width: 'w-[100px]' },
    { key: 'calendar', label: 'Calendar', badge: true, width: 'w-[100px]' },
  ],
}

// Badge colour maps -------------------------------------------------

export const BADGE_COLORS: Record<string, string> = {
  // Jira issue types
  Bug: '#ef4444',
  Task: '#3b82f6',
  Story: '#22c55e',
  Epic: '#a855f7',
  Spike: '#e8a84c',
  // Jira statuses
  'To Do': '#8a8680',
  'In Progress': '#4a9a9a',
  'In Review': '#e8a84c',
  Done: '#22c55e',
  // Drive file types
  'Google Doc': '#3b82f6',
  'Google Sheet': '#22c55e',
  'Google Slides': '#e8a84c',
  PDF: '#ef4444',
  Folder: '#8a8680',
  File: '#8a8680',
  // Confluence spaces
  Engineering: '#2684ff',
  Operations: '#e8a84c',
  Product: '#22c55e',
  TMA: '#a855f7',
  // Calendars
  Work: '#4a9a9a',
  Personal: '#a855f7',
}

// Filters ------------------------------------------------------------

export type FilterState = Record<string, string[]>

/** Filter pill sets per domain (open decision #2 — full sets, recommended). */
export const DOMAIN_FILTERS: Record<DomainKey, Record<string, string[]>> = {
  email: {
    Sender: ['GitHub', 'Stripe', 'Hetzner', 'Atlassian', 'Acme Corp'],
    Label: ['Actionable', 'Information', 'Waiting'],
    Attachments: ['Has attachment'],
  },
  drive: {
    'File type': ['Google Doc', 'Google Sheet', 'Google Slides', 'PDF', 'Folder'],
    Modified: ['Today', 'This week', 'This month'],
  },
  confluence: {
    Space: ['Engineering', 'Operations', 'Product', 'TMA'],
    Status: ['Current', 'Draft', 'Archived'],
  },
  jira: {
    Project: ['ZEN', 'TMA', 'INFRA'],
    Type: ['Bug', 'Task', 'Story', 'Epic'],
    Status: ['To Do', 'In Progress', 'In Review', 'Done'],
    Priority: ['Highest', 'High', 'Medium', 'Low'],
  },
  calendar: {
    Calendar: ['Work', 'Personal'],
    When: ['Today', 'This week', 'Upcoming', 'Past'],
  },
}

// Records ------------------------------------------------------------

export interface DataRecord {
  id: string
  domain: DomainKey
  /** Common column: title. */
  title: string
  /** Common column: the domain's own date (sent / modified / edited / updated / start). */
  date: string
  /** When the Nango→Convex pipeline last synced this record. */
  syncedAt: string
  /** Link back to the source system (null = known gap, e.g. Confluence webUrl). */
  externalUrl: string | null
  /** Domain-specific column values, keyed by ColumnDef.key. */
  cells: Record<string, string>
  /** Full field list for the detail panel. */
  detail: { label: string; value: string }[]
  /** Content preview for the detail panel (email body / page content / description). */
  body?: string
  /** Filter-matching metadata. */
  meta: Record<string, string[]>
}

/** Semantic search result wrapper (Confluence / Drive). */
export interface SemanticHit {
  record: DataRecord
  relevance: number
  excerpt: string
}

// Helpers -------------------------------------------------------------

export const NOW = new Date('2026-06-03T14:00:00Z').getTime()
export const STALE_AFTER_MS = 7 * 24 * 60 * 60 * 1000

export function timeAgo(iso: string): string {
  const diff = NOW - new Date(iso).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days === 1) return 'yesterday'
  if (days < 60) return `${days}d ago`
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

export function isStale(iso: string): boolean {
  return NOW - new Date(iso).getTime() > STALE_AFTER_MS
}

export function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

export function shortDateTime(iso: string): string {
  const d = new Date(iso)
  return (
    d.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' }) +
    ' ' +
    d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
  )
}

// =====================================================================
// Mock dataset
// =====================================================================
// Email + Confluence are stale (synced 2026-04-22 — mirrors the real stalled
// pipelines). Drive / Jira / Calendar are fresh.

const STALE_SYNC = '2026-04-22T08:14:00Z'
const FRESH_SYNC = '2026-06-03T11:42:00Z'

const EMAIL_RECORDS: DataRecord[] = [
  {
    id: 'em-1', domain: 'email',
    title: '[GitHub] Security alert: dependency vulnerability in zenbot.chat',
    date: '2026-04-22T07:55:00Z', syncedAt: STALE_SYNC,
    externalUrl: 'https://mail.google.com/mail/u/0/#all/18f2a1',
    cells: { sender: 'GitHub', date: '22 Apr' },
    detail: [
      { label: 'Subject', value: '[GitHub] Security alert: dependency vulnerability in zenbot.chat' },
      { label: 'From', value: 'GitHub <noreply@github.com>' },
      { label: 'To', value: 'matt@tinymedicalapps.com' },
      { label: 'Date', value: '22 Apr 2026, 08:55' },
      { label: 'Labels', value: 'Actionable' },
    ],
    body: 'We found a potential security vulnerability in one of your dependencies.\n\nRepository: contextloops/zenbot.chat\nPackage: next (npm)\nSeverity: moderate\n\nA bump to 15.2.4 resolves the advisory. Dependabot can open a PR for this update.',
    meta: { Sender: ['GitHub'], Label: ['Actionable'], Attachments: [] },
  },
  {
    id: 'em-2', domain: 'email',
    title: 'Re: TMA patient onboarding — next steps',
    date: '2026-04-22T06:30:00Z', syncedAt: STALE_SYNC,
    externalUrl: 'https://mail.google.com/mail/u/0/#all/18f29e',
    cells: { sender: 'Sarah Chen', date: '22 Apr' },
    detail: [
      { label: 'Subject', value: 'Re: TMA patient onboarding — next steps' },
      { label: 'From', value: 'Sarah Chen <sarah@tinymedicalapps.com>' },
      { label: 'To', value: 'matt@tinymedicalapps.com' },
      { label: 'Date', value: '22 Apr 2026, 07:30' },
      { label: 'Labels', value: 'Actionable, Waiting' },
    ],
    body: 'Thanks Matt — agreed on splitting the onboarding flow into clinical and admin tracks.\n\nCan you confirm the NHS login spike is still planned for this sprint? Once that lands I can finalise the wireframes.\n\nSarah',
    meta: { Sender: [], Label: ['Actionable', 'Waiting'], Attachments: [] },
  },
  {
    id: 'em-3', domain: 'email',
    title: 'Jira: ZEN-142 assigned to you',
    date: '2026-04-21T16:20:00Z', syncedAt: STALE_SYNC,
    externalUrl: 'https://mail.google.com/mail/u/0/#all/18f284',
    cells: { sender: 'Atlassian', date: '21 Apr' },
    detail: [
      { label: 'Subject', value: 'Jira: ZEN-142 assigned to you' },
      { label: 'From', value: 'Atlassian <jira@contextloops.atlassian.net>' },
      { label: 'To', value: 'matt@tinymedicalapps.com' },
      { label: 'Date', value: '21 Apr 2026, 17:20' },
      { label: 'Labels', value: 'Information' },
    ],
    body: 'Tom Harris assigned ZEN-142 "Rate limiting for /sync endpoint" to you.\n\nPriority: High\nSprint: Sprint 22',
    meta: { Sender: ['Atlassian'], Label: ['Information'], Attachments: [] },
  },
  {
    id: 'em-4', domain: 'email',
    title: 'Your Hetzner invoice for April 2026',
    date: '2026-04-21T09:02:00Z', syncedAt: STALE_SYNC,
    externalUrl: 'https://mail.google.com/mail/u/0/#all/18f270',
    cells: { sender: 'Hetzner', date: '21 Apr' },
    detail: [
      { label: 'Subject', value: 'Your Hetzner invoice for April 2026' },
      { label: 'From', value: 'Hetzner Online <invoice@hetzner.com>' },
      { label: 'To', value: 'matt@tinymedicalapps.com' },
      { label: 'Date', value: '21 Apr 2026, 10:02' },
      { label: 'Labels', value: 'Information' },
      { label: 'Attachments', value: 'invoice-2026-04.pdf (84 KB)' },
    ],
    body: 'Dear Matt Bourne,\n\nYour invoice for April 2026 is attached.\n\nTotal: €43.18\nServers: Containers (CAX31), Containers2 (CPX31)\n\nThe amount will be debited from your registered payment method.',
    meta: { Sender: ['Hetzner'], Label: ['Information'], Attachments: ['Has attachment'] },
  },
  {
    id: 'em-5', domain: 'email',
    title: 'Stripe receipt: OpenAI API — $156.80',
    date: '2026-04-20T03:15:00Z', syncedAt: STALE_SYNC,
    externalUrl: 'https://mail.google.com/mail/u/0/#all/18f24b',
    cells: { sender: 'Stripe', date: '20 Apr' },
    detail: [
      { label: 'Subject', value: 'Stripe receipt: OpenAI API — $156.80' },
      { label: 'From', value: 'Stripe <receipts@stripe.com>' },
      { label: 'To', value: 'matt@tinymedicalapps.com' },
      { label: 'Date', value: '20 Apr 2026, 04:15' },
      { label: 'Labels', value: 'Information' },
    ],
    body: 'Receipt from OpenAI, LLC\n\nAmount paid: $156.80\nDate: April 20, 2026\nPayment method: Visa ending 4242\n\nUsage: API — GPT-4o-mini realtime, text-embedding-3-small',
    meta: { Sender: ['Stripe'], Label: ['Information'], Attachments: [] },
  },
  {
    id: 'em-6', domain: 'email',
    title: 'Q2 budget review — agenda attached',
    date: '2026-04-17T14:40:00Z', syncedAt: STALE_SYNC,
    externalUrl: 'https://mail.google.com/mail/u/0/#all/18f1d2',
    cells: { sender: 'James Wright', date: '17 Apr' },
    detail: [
      { label: 'Subject', value: 'Q2 budget review — agenda attached' },
      { label: 'From', value: 'James Wright <james@acme.com>' },
      { label: 'To', value: 'matt@tinymedicalapps.com' },
      { label: 'Date', value: '17 Apr 2026, 15:40' },
      { label: 'Labels', value: 'Actionable' },
      { label: 'Attachments', value: 'q2-agenda.docx (32 KB)' },
    ],
    body: 'Hi Matt,\n\nAhead of our Q2 budget review I have attached the agenda. Key items:\n\n1. Infrastructure spend (Hetzner + AI APIs)\n2. Contract renewal for the Acme partnership\n3. Headcount for the TMA clinical team\n\nBest,\nJames',
    meta: { Sender: ['Acme Corp'], Label: ['Actionable'], Attachments: ['Has attachment'] },
  },
  {
    id: 'em-7', domain: 'email',
    title: 'Your Coolify deployment failed: zenbot-web',
    date: '2026-04-19T22:08:00Z', syncedAt: STALE_SYNC,
    externalUrl: 'https://mail.google.com/mail/u/0/#all/18f22a',
    cells: { sender: 'Coolify', date: '19 Apr' },
    detail: [
      { label: 'Subject', value: 'Your Coolify deployment failed: zenbot-web' },
      { label: 'From', value: 'Coolify <alerts@coolify.io>' },
      { label: 'To', value: 'matt@tinymedicalapps.com' },
      { label: 'Date', value: '19 Apr 2026, 23:08' },
      { label: 'Labels', value: 'Actionable' },
    ],
    body: 'Deployment of zenbot-web (commit 4f2a1c9) failed.\n\nError: Docker build exited with code 1\nStep: COPY packages/store/ ./packages/store/\n\nView the full build log in the Coolify dashboard.',
    meta: { Sender: [], Label: ['Actionable'], Attachments: [] },
  },
  {
    id: 'em-8', domain: 'email',
    title: 'Confluence digest: 4 pages updated this week',
    date: '2026-04-18T08:00:00Z', syncedAt: STALE_SYNC,
    externalUrl: 'https://mail.google.com/mail/u/0/#all/18f1f8',
    cells: { sender: 'Atlassian', date: '18 Apr' },
    detail: [
      { label: 'Subject', value: 'Confluence digest: 4 pages updated this week' },
      { label: 'From', value: 'Atlassian <confluence@contextloops.atlassian.net>' },
      { label: 'To', value: 'matt@tinymedicalapps.com' },
      { label: 'Date', value: '18 Apr 2026, 09:00' },
      { label: 'Labels', value: 'Information' },
    ],
    body: 'Pages updated in your spaces this week:\n\n• API Rate Limiting RFC (Engineering)\n• Zenbot Architecture Overview (Engineering)\n• Q2 OKRs (Product)\n• Release Notes — April (Product)',
    meta: { Sender: ['Atlassian'], Label: ['Information'], Attachments: [] },
  },
]

const DRIVE_RECORDS: DataRecord[] = [
  {
    id: 'dr-1', domain: 'drive',
    title: 'ZenBot Product Roadmap H2',
    date: '2026-06-03T09:15:00Z', syncedAt: FRESH_SYNC,
    externalUrl: 'https://docs.google.com/document/d/1aBc',
    cells: { type: 'Google Doc', size: '—', date: '3 Jun' },
    detail: [
      { label: 'Name', value: 'ZenBot Product Roadmap H2' },
      { label: 'Type', value: 'Google Doc' },
      { label: 'Modified', value: '3 Jun 2026, 10:15' },
      { label: 'Created', value: '12 May 2026' },
      { label: 'Owner', value: 'matt@tinymedicalapps.com' },
    ],
    body: '# ZenBot Product Roadmap H2\n\n## Themes\n\n1. Commercial readiness — hosted cloud onboarding for SMEs\n2. Data plane completeness — every synced source browsable + searchable\n3. Desktop app (Electrobun) — replace web prototype\n\n## Q3\n\n- Data page (tables + vector search)\n- Skills marketplace slice…',
    meta: { 'File type': ['Google Doc'], Modified: ['Today', 'This week', 'This month'] },
  },
  {
    id: 'dr-2', domain: 'drive',
    title: 'Q2 Budget 2026',
    date: '2026-06-02T16:40:00Z', syncedAt: FRESH_SYNC,
    externalUrl: 'https://docs.google.com/spreadsheets/d/1dEf',
    cells: { type: 'Google Sheet', size: '245 KB', date: '2 Jun' },
    detail: [
      { label: 'Name', value: 'Q2 Budget 2026' },
      { label: 'Type', value: 'Google Sheet' },
      { label: 'Modified', value: '2 Jun 2026, 17:40' },
      { label: 'Created', value: '28 Mar 2026' },
      { label: 'Size', value: '245 KB' },
      { label: 'Owner', value: 'matt@tinymedicalapps.com' },
    ],
    body: 'Sheets: Overview · Infrastructure · AI APIs · Salaries · Forecast\n\nInfrastructure running total: £1,840 / quarter\nAI API spend: £470 / month (trending +12%)',
    meta: { 'File type': ['Google Sheet'], Modified: ['This week', 'This month'] },
  },
  {
    id: 'dr-3', domain: 'drive',
    title: 'Meeting notes — Acme partnership',
    date: '2026-06-01T11:20:00Z', syncedAt: FRESH_SYNC,
    externalUrl: 'https://docs.google.com/document/d/1gHi',
    cells: { type: 'Google Doc', size: '—', date: '1 Jun' },
    detail: [
      { label: 'Name', value: 'Meeting notes — Acme partnership' },
      { label: 'Type', value: 'Google Doc' },
      { label: 'Modified', value: '1 Jun 2026, 12:20' },
      { label: 'Created', value: '1 Jun 2026' },
      { label: 'Owner', value: 'matt@tinymedicalapps.com' },
    ],
    body: '# Acme partnership — 1 Jun\n\nAttendees: Matt, James Wright\n\n- Contract section 4.2 (data residency) needs legal review\n- Acme wants EU-hosted deployment — Hetzner works\n- Next call: 5 Jun',
    meta: { 'File type': ['Google Doc'], Modified: ['This week', 'This month'] },
  },
  {
    id: 'dr-4', domain: 'drive',
    title: 'Patient Onboarding Flows',
    date: '2026-05-30T15:00:00Z', syncedAt: FRESH_SYNC,
    externalUrl: 'https://docs.google.com/presentation/d/1jKl',
    cells: { type: 'Google Slides', size: '—', date: '30 May' },
    detail: [
      { label: 'Name', value: 'Patient Onboarding Flows' },
      { label: 'Type', value: 'Google Slides' },
      { label: 'Modified', value: '30 May 2026, 16:00' },
      { label: 'Created', value: '14 May 2026' },
      { label: 'Owner', value: 'sarah@tinymedicalapps.com' },
    ],
    body: '14 slides. Clinical track vs admin track split, NHS login integration points, consent screens.',
    meta: { 'File type': ['Google Slides'], Modified: ['This month'] },
  },
  {
    id: 'dr-5', domain: 'drive',
    title: 'TMA Clinical Safety Case.pdf',
    date: '2026-05-28T10:30:00Z', syncedAt: FRESH_SYNC,
    externalUrl: 'https://drive.google.com/file/d/1mNo',
    cells: { type: 'PDF', size: '1.2 MB', date: '28 May' },
    detail: [
      { label: 'Name', value: 'TMA Clinical Safety Case.pdf' },
      { label: 'Type', value: 'PDF' },
      { label: 'Modified', value: '28 May 2026, 11:30' },
      { label: 'Created', value: '28 May 2026' },
      { label: 'Size', value: '1.2 MB' },
      { label: 'Owner', value: 'matt@tinymedicalapps.com' },
    ],
    body: 'DCB0129 clinical safety case for the TMA patient onboarding system. Hazard log, clinical risk management plan, safety case report. Converted to markdown + indexed (markitdown worker).',
    meta: { 'File type': ['PDF'], Modified: ['This month'] },
  },
  {
    id: 'dr-6', domain: 'drive',
    title: 'Brand Guidelines v3.pdf',
    date: '2026-05-22T09:45:00Z', syncedAt: FRESH_SYNC,
    externalUrl: 'https://drive.google.com/file/d/1pQr',
    cells: { type: 'PDF', size: '4.8 MB', date: '22 May' },
    detail: [
      { label: 'Name', value: 'Brand Guidelines v3.pdf' },
      { label: 'Type', value: 'PDF' },
      { label: 'Modified', value: '22 May 2026, 10:45' },
      { label: 'Created', value: '22 May 2026' },
      { label: 'Size', value: '4.8 MB' },
      { label: 'Owner', value: 'matt@tinymedicalapps.com' },
    ],
    body: 'Logo usage, colour palette (zen dark theme), typography, voice + tone for ZenBot and TMA brands.',
    meta: { 'File type': ['PDF'], Modified: ['This month'] },
  },
  {
    id: 'dr-7', domain: 'drive',
    title: 'Invoices / April 2026',
    date: '2026-05-01T08:00:00Z', syncedAt: FRESH_SYNC,
    externalUrl: 'https://drive.google.com/drive/folders/1sTu',
    cells: { type: 'Folder', size: '—', date: '1 May' },
    detail: [
      { label: 'Name', value: 'Invoices / April 2026' },
      { label: 'Type', value: 'Folder' },
      { label: 'Modified', value: '1 May 2026, 09:00' },
      { label: 'Created', value: '1 Apr 2026' },
      { label: 'Owner', value: 'matt@tinymedicalapps.com' },
    ],
    body: 'Contains 14 files: Hetzner, OpenAI, Stripe fees, Google Workspace, accountant exports.',
    meta: { 'File type': ['Folder'], Modified: [] },
  },
  {
    id: 'dr-8', domain: 'drive',
    title: 'contextloops-architecture.drawio',
    date: '2026-05-25T13:10:00Z', syncedAt: FRESH_SYNC,
    externalUrl: 'https://drive.google.com/file/d/1vWx',
    cells: { type: 'File', size: '68 KB', date: '25 May' },
    detail: [
      { label: 'Name', value: 'contextloops-architecture.drawio' },
      { label: 'Type', value: 'File' },
      { label: 'Modified', value: '25 May 2026, 14:10' },
      { label: 'Created', value: '20 May 2026' },
      { label: 'Size', value: '68 KB' },
      { label: 'Owner', value: 'matt@tinymedicalapps.com' },
    ],
    body: 'Container management architecture: colima + coolify wrapper, local vs Hetzner placement, zenbot container group.',
    meta: { 'File type': [], Modified: ['This month'] },
  },
]

const CONFLUENCE_RECORDS: DataRecord[] = [
  {
    id: 'cf-1', domain: 'confluence',
    title: 'API Rate Limiting RFC',
    date: '2026-04-12T12:31:00Z', syncedAt: STALE_SYNC,
    externalUrl: null,
    cells: { space: 'Engineering', status: 'Current', date: '12 Apr' },
    detail: [
      { label: 'Title', value: 'API Rate Limiting RFC' },
      { label: 'Space', value: 'Engineering / RFCs' },
      { label: 'Status', value: 'Current' },
      { label: 'Author', value: 'Sarah Chen' },
      { label: 'Last edited', value: '12 Apr 2026' },
      { label: 'Created', value: '8 Apr 2026' },
    ],
    body: '# API Rate Limiting RFC\n\n## Problem\n\nThe /sync endpoint is being hammered by aggressive client retries during cold-start windows, occasionally pushing Convex over its concurrent-mutation budget.\n\n## Proposal\n\nIntroduce a token-bucket limiter keyed on (workspace_id, endpoint) with a default of 60 req/min and a burst of 20.',
    meta: { Space: ['Engineering'], Status: ['Current'] },
  },
  {
    id: 'cf-2', domain: 'confluence',
    title: 'Zenbot Architecture Overview',
    date: '2026-04-20T09:00:00Z', syncedAt: STALE_SYNC,
    externalUrl: null,
    cells: { space: 'Engineering', status: 'Current', date: '20 Apr' },
    detail: [
      { label: 'Title', value: 'Zenbot Architecture Overview' },
      { label: 'Space', value: 'Engineering / Architecture' },
      { label: 'Status', value: 'Current' },
      { label: 'Author', value: 'Matt Bourne' },
      { label: 'Last edited', value: '20 Apr 2026' },
      { label: 'Created', value: '2 Feb 2026' },
    ],
    body: '# Zenbot Architecture Overview\n\nZenbot is a personal assistant with three planes: voice, text chat, and async tasks.\n\n## Planes\n\n- Voice — LiveKit + Gemini realtime\n- Text chat — OpenRouter (Claude Haiku 4.5)\n- Async tasks — Termo + Codeman\n\nAll data flows through Convex.',
    meta: { Space: ['Engineering'], Status: ['Current'] },
  },
  {
    id: 'cf-3', domain: 'confluence',
    title: 'Release Notes — April',
    date: '2026-04-21T17:30:00Z', syncedAt: STALE_SYNC,
    externalUrl: null,
    cells: { space: 'Product', status: 'Draft', date: '21 Apr' },
    detail: [
      { label: 'Title', value: 'Release Notes — April' },
      { label: 'Space', value: 'Product' },
      { label: 'Status', value: 'Draft' },
      { label: 'Author', value: 'Matt Bourne' },
      { label: 'Last edited', value: '21 Apr 2026' },
      { label: 'Created', value: '21 Apr 2026' },
    ],
    body: '# Release Notes — April\n\n- Email sender classification (Claude Haiku batch)\n- Gmail filter management\n- Magic nav shell replaces top-bar pattern\n- Sync history page (mockup)',
    meta: { Space: ['Product'], Status: ['Draft'] },
  },
  {
    id: 'cf-4', domain: 'confluence',
    title: 'Q2 OKRs',
    date: '2026-04-15T10:00:00Z', syncedAt: STALE_SYNC,
    externalUrl: null,
    cells: { space: 'Product', status: 'Current', date: '15 Apr' },
    detail: [
      { label: 'Title', value: 'Q2 OKRs' },
      { label: 'Space', value: 'Product' },
      { label: 'Status', value: 'Current' },
      { label: 'Author', value: 'Matt Bourne' },
      { label: 'Last edited', value: '15 Apr 2026' },
      { label: 'Created', value: '1 Apr 2026' },
    ],
    body: '# Q2 OKRs\n\n## O1: Data plane completeness\n- KR1: Every synced source browsable in the Data page\n- KR2: Vector search on content-indexed domains\n\n## O2: Commercial readiness\n- KR1: Hosted cloud onboarding flow\n- KR2: DIY self-hosting docs',
    meta: { Space: ['Product'], Status: ['Current'] },
  },
  {
    id: 'cf-5', domain: 'confluence',
    title: 'Runbook: Certificate Rotation',
    date: '2026-04-10T14:20:00Z', syncedAt: STALE_SYNC,
    externalUrl: null,
    cells: { space: 'Operations', status: 'Current', date: '10 Apr' },
    detail: [
      { label: 'Title', value: 'Runbook: Certificate Rotation' },
      { label: 'Space', value: 'Operations / Runbooks' },
      { label: 'Status', value: 'Current' },
      { label: 'Author', value: 'Lisa Park' },
      { label: 'Last edited', value: '10 Apr 2026' },
      { label: 'Created', value: '3 Mar 2026' },
    ],
    body: '# Runbook: Certificate Rotation\n\nLetsEncrypt certs auto-renew 30 days before expiry. This runbook covers the manual fallback when the renew job fails.\n\n1. SSH to the Coolify host\n2. certbot renew --force-renewal\n3. Reload Caddy via coolify cli redeploy',
    meta: { Space: ['Operations'], Status: ['Current'] },
  },
  {
    id: 'cf-6', domain: 'confluence',
    title: 'Incident Postmortem: 2026-04-02 outage',
    date: '2026-04-05T16:45:00Z', syncedAt: STALE_SYNC,
    externalUrl: null,
    cells: { space: 'Operations', status: 'Current', date: '5 Apr' },
    detail: [
      { label: 'Title', value: 'Incident Postmortem: 2026-04-02 outage' },
      { label: 'Space', value: 'Operations' },
      { label: 'Status', value: 'Current' },
      { label: 'Author', value: 'Matt Bourne' },
      { label: 'Last edited', value: '5 Apr 2026' },
      { label: 'Created', value: '3 Apr 2026' },
    ],
    body: '# Incident Postmortem\n\n## Timeline\n\n02:14 — LiveKit workers stopped accepting connections\n02:40 — Phantom worker registration identified\n03:05 — LiveKit app restarted via Coolify, service restored\n\n## Root cause\n\nStale worker registrations after an unclean container restart.',
    meta: { Space: ['Operations'], Status: ['Current'] },
  },
  {
    id: 'cf-7', domain: 'confluence',
    title: 'Patient Data Handling Policy',
    date: '2026-04-08T11:00:00Z', syncedAt: STALE_SYNC,
    externalUrl: null,
    cells: { space: 'TMA', status: 'Current', date: '8 Apr' },
    detail: [
      { label: 'Title', value: 'Patient Data Handling Policy' },
      { label: 'Space', value: 'TMA / Compliance' },
      { label: 'Status', value: 'Current' },
      { label: 'Author', value: 'Matt Bourne' },
      { label: 'Last edited', value: '8 Apr 2026' },
      { label: 'Created', value: '15 Jan 2026' },
    ],
    body: '# Patient Data Handling Policy\n\nGDPR + NHS DSP Toolkit requirements for patient data in TMA systems. Data residency: UK/EU only. Retention: 8 years post-discharge. Access: role-based, audited.',
    meta: { Space: ['TMA'], Status: ['Current'] },
  },
  {
    id: 'cf-8', domain: 'confluence',
    title: 'Onboarding: New Engineers',
    date: '2026-04-18T09:30:00Z', syncedAt: STALE_SYNC,
    externalUrl: null,
    cells: { space: 'Engineering', status: 'Draft', date: '18 Apr' },
    detail: [
      { label: 'Title', value: 'Onboarding: New Engineers' },
      { label: 'Space', value: 'Engineering' },
      { label: 'Status', value: 'Draft' },
      { label: 'Author', value: 'Tom Harris' },
      { label: 'Last edited', value: '18 Apr 2026' },
      { label: 'Created', value: '18 Apr 2026' },
    ],
    body: '# Onboarding: New Engineers\n\nDev environment setup, repo access, architecture walkthrough, first-week tasks. (Draft — sections on Convex + Nango still to write.)',
    meta: { Space: ['Engineering'], Status: ['Draft'] },
  },
]

const JIRA_RECORDS: DataRecord[] = [
  {
    id: 'ji-1', domain: 'jira',
    title: 'Convex zombie query guard',
    date: '2026-06-03T10:20:00Z', syncedAt: FRESH_SYNC,
    externalUrl: 'https://contextloops.atlassian.net/browse/ZEN-151',
    cells: { key: 'ZEN-151', type: 'Bug', status: 'In Progress', priority: 'Highest', assignee: 'Tom', date: '3 Jun' },
    detail: [
      { label: 'Key', value: 'ZEN-151' },
      { label: 'Project', value: 'ZenBot (ZEN)' },
      { label: 'Type', value: 'Bug' },
      { label: 'Status', value: 'In Progress' },
      { label: 'Priority', value: 'Highest' },
      { label: 'Assignee', value: 'Tom Harris' },
      { label: 'Sprint', value: 'Sprint 23' },
      { label: 'Updated', value: '3 Jun 2026' },
      { label: 'Created', value: '3 Jun 2026' },
    ],
    body: 'Timed-out Convex queries keep running server-side as zombie executions, compounding load. Add a client-side guard: back off on first timeout, never retry-loop. Triggered by the 2026-06-03 backend incident.',
    meta: { Project: ['ZEN'], Type: ['Bug'], Status: ['In Progress'], Priority: ['Highest'] },
  },
  {
    id: 'ji-2', domain: 'jira',
    title: 'Data page table redesign',
    date: '2026-06-03T08:45:00Z', syncedAt: FRESH_SYNC,
    externalUrl: 'https://contextloops.atlassian.net/browse/ZEN-156',
    cells: { key: 'ZEN-156', type: 'Story', status: 'To Do', priority: 'Medium', assignee: 'Matt', date: '3 Jun' },
    detail: [
      { label: 'Key', value: 'ZEN-156' },
      { label: 'Project', value: 'ZenBot (ZEN)' },
      { label: 'Type', value: 'Story' },
      { label: 'Status', value: 'To Do' },
      { label: 'Priority', value: 'Medium' },
      { label: 'Assignee', value: 'Matt Bourne' },
      { label: 'Sprint', value: 'Sprint 23' },
      { label: 'Updated', value: '3 Jun 2026' },
      { label: 'Created', value: '3 Jun 2026' },
    ],
    body: 'Port the approved Magic Patterns mockup into web/: table-first layout per domain, keyword search, filter pills, pagination, links back to source. PLAN.md Phase C.',
    meta: { Project: ['ZEN'], Type: ['Story'], Status: ['To Do'], Priority: ['Medium'] },
  },
  {
    id: 'ji-3', domain: 'jira',
    title: 'Rate limiting for /sync endpoint',
    date: '2026-06-02T15:30:00Z', syncedAt: FRESH_SYNC,
    externalUrl: 'https://contextloops.atlassian.net/browse/ZEN-142',
    cells: { key: 'ZEN-142', type: 'Bug', status: 'In Progress', priority: 'High', assignee: 'Tom', date: '2 Jun' },
    detail: [
      { label: 'Key', value: 'ZEN-142' },
      { label: 'Project', value: 'ZenBot (ZEN)' },
      { label: 'Type', value: 'Bug' },
      { label: 'Status', value: 'In Progress' },
      { label: 'Priority', value: 'High' },
      { label: 'Assignee', value: 'Tom Harris' },
      { label: 'Sprint', value: 'Sprint 22' },
      { label: 'Updated', value: '2 Jun 2026' },
      { label: 'Created', value: '3 Apr 2026' },
    ],
    body: 'The /sync endpoint needs a token-bucket limiter. See the API Rate Limiting RFC in Confluence for the design.',
    meta: { Project: ['ZEN'], Type: ['Bug'], Status: ['In Progress'], Priority: ['High'] },
  },
  {
    id: 'ji-4', domain: 'jira',
    title: 'Patient onboarding redesign',
    date: '2026-06-02T11:00:00Z', syncedAt: FRESH_SYNC,
    externalUrl: 'https://contextloops.atlassian.net/browse/TMA-89',
    cells: { key: 'TMA-89', type: 'Story', status: 'In Progress', priority: 'Medium', assignee: 'Sarah', date: '2 Jun' },
    detail: [
      { label: 'Key', value: 'TMA-89' },
      { label: 'Project', value: 'Tiny Medical Apps (TMA)' },
      { label: 'Type', value: 'Story' },
      { label: 'Status', value: 'In Progress' },
      { label: 'Priority', value: 'Medium' },
      { label: 'Assignee', value: 'Sarah Chen' },
      { label: 'Sprint', value: 'Sprint 22' },
      { label: 'Updated', value: '2 Jun 2026' },
      { label: 'Created', value: '20 Mar 2026' },
    ],
    body: 'Split patient onboarding into clinical and admin tracks. Blocked on the NHS login integration spike (TMA-92).',
    meta: { Project: ['TMA'], Type: ['Story'], Status: ['In Progress'], Priority: ['Medium'] },
  },
  {
    id: 'ji-5', domain: 'jira',
    title: 'Split GmailEmail bodies into record_bodies table',
    date: '2026-06-01T09:10:00Z', syncedAt: FRESH_SYNC,
    externalUrl: 'https://contextloops.atlassian.net/browse/ZEN-149',
    cells: { key: 'ZEN-149', type: 'Task', status: 'To Do', priority: 'High', assignee: '—', date: '1 Jun' },
    detail: [
      { label: 'Key', value: 'ZEN-149' },
      { label: 'Project', value: 'ZenBot (ZEN)' },
      { label: 'Type', value: 'Task' },
      { label: 'Status', value: 'To Do' },
      { label: 'Priority', value: 'High' },
      { label: 'Assignee', value: 'Unassigned' },
      { label: 'Updated', value: '1 Jun 2026' },
      { label: 'Created', value: '1 Jun 2026' },
    ],
    body: 'Gmail records carry full email bodies inline, making every GmailEmail read the heaviest in the system. Split bodies into any_zenbot_record_bodies the way Confluence already got.',
    meta: { Project: ['ZEN'], Type: ['Task'], Status: ['To Do'], Priority: ['High'] },
  },
  {
    id: 'ji-6', domain: 'jira',
    title: 'SSL cert renewal automation',
    date: '2026-05-30T14:25:00Z', syncedAt: FRESH_SYNC,
    externalUrl: 'https://contextloops.atlassian.net/browse/ZEN-138',
    cells: { key: 'ZEN-138', type: 'Task', status: 'In Review', priority: 'High', assignee: 'Lisa', date: '30 May' },
    detail: [
      { label: 'Key', value: 'ZEN-138' },
      { label: 'Project', value: 'ZenBot (ZEN)' },
      { label: 'Type', value: 'Task' },
      { label: 'Status', value: 'In Review' },
      { label: 'Priority', value: 'High' },
      { label: 'Assignee', value: 'Lisa Park' },
      { label: 'Sprint', value: 'Sprint 22' },
      { label: 'Updated', value: '30 May 2026' },
      { label: 'Created', value: '1 Apr 2026' },
    ],
    body: 'Automate the LetsEncrypt renewal fallback so the runbook is never needed manually.',
    meta: { Project: ['ZEN'], Type: ['Task'], Status: ['In Review'], Priority: ['High'] },
  },
  {
    id: 'ji-7', domain: 'jira',
    title: 'NHS login integration spike',
    date: '2026-05-29T16:00:00Z', syncedAt: FRESH_SYNC,
    externalUrl: 'https://contextloops.atlassian.net/browse/TMA-92',
    cells: { key: 'TMA-92', type: 'Task', status: 'To Do', priority: 'Medium', assignee: '—', date: '29 May' },
    detail: [
      { label: 'Key', value: 'TMA-92' },
      { label: 'Project', value: 'Tiny Medical Apps (TMA)' },
      { label: 'Type', value: 'Task' },
      { label: 'Status', value: 'To Do' },
      { label: 'Priority', value: 'Medium' },
      { label: 'Assignee', value: 'Unassigned' },
      { label: 'Updated', value: '29 May 2026' },
      { label: 'Created', value: '29 May 2026' },
    ],
    body: 'Investigate NHS login OIDC integration: registration requirements, sandbox environment, conformance testing.',
    meta: { Project: ['TMA'], Type: ['Task'], Status: ['To Do'], Priority: ['Medium'] },
  },
  {
    id: 'ji-8', domain: 'jira',
    title: 'Care plan templates',
    date: '2026-05-31T10:40:00Z', syncedAt: FRESH_SYNC,
    externalUrl: 'https://contextloops.atlassian.net/browse/TMA-95',
    cells: { key: 'TMA-95', type: 'Epic', status: 'To Do', priority: 'Medium', assignee: 'Sarah', date: '31 May' },
    detail: [
      { label: 'Key', value: 'TMA-95' },
      { label: 'Project', value: 'Tiny Medical Apps (TMA)' },
      { label: 'Type', value: 'Epic' },
      { label: 'Status', value: 'To Do' },
      { label: 'Priority', value: 'Medium' },
      { label: 'Assignee', value: 'Sarah Chen' },
      { label: 'Updated', value: '31 May 2026' },
      { label: 'Created', value: '31 May 2026' },
    ],
    body: 'Epic: clinician-authored care plan templates with variable substitution and patient-facing rendering.',
    meta: { Project: ['TMA'], Type: ['Epic'], Status: ['To Do'], Priority: ['Medium'] },
  },
  {
    id: 'ji-9', domain: 'jira',
    title: 'Nango webhook pipeline',
    date: '2026-05-28T12:15:00Z', syncedAt: FRESH_SYNC,
    externalUrl: 'https://contextloops.atlassian.net/browse/ZEN-135',
    cells: { key: 'ZEN-135', type: 'Story', status: 'Done', priority: 'Medium', assignee: 'Matt', date: '28 May' },
    detail: [
      { label: 'Key', value: 'ZEN-135' },
      { label: 'Project', value: 'ZenBot (ZEN)' },
      { label: 'Type', value: 'Story' },
      { label: 'Status', value: 'Done' },
      { label: 'Priority', value: 'Medium' },
      { label: 'Assignee', value: 'Matt Bourne' },
      { label: 'Sprint', value: 'Sprint 22' },
      { label: 'Updated', value: '28 May 2026' },
      { label: 'Created', value: '25 Mar 2026' },
    ],
    body: 'Nango fires a webhook on sync complete; Convex pulls records directly from the Nango API and upserts with dedup by externalId + model.',
    meta: { Project: ['ZEN'], Type: ['Story'], Status: ['Done'], Priority: ['Medium'] },
  },
  {
    id: 'ji-10', domain: 'jira',
    title: 'Hetzner ARM64 node upgrade',
    date: '2026-05-26T09:00:00Z', syncedAt: FRESH_SYNC,
    externalUrl: 'https://contextloops.atlassian.net/browse/INFRA-41',
    cells: { key: 'INFRA-41', type: 'Task', status: 'Done', priority: 'Low', assignee: 'Matt', date: '26 May' },
    detail: [
      { label: 'Key', value: 'INFRA-41' },
      { label: 'Project', value: 'Infrastructure (INFRA)' },
      { label: 'Type', value: 'Task' },
      { label: 'Status', value: 'Done' },
      { label: 'Priority', value: 'Low' },
      { label: 'Assignee', value: 'Matt Bourne' },
      { label: 'Updated', value: '26 May 2026' },
      { label: 'Created', value: '20 May 2026' },
    ],
    body: 'Upgrade the Containers server (ARM64) to CAX41 for Convex headroom.',
    meta: { Project: ['INFRA'], Type: ['Task'], Status: ['Done'], Priority: ['Low'] },
  },
]

const CALENDAR_RECORDS: DataRecord[] = [
  {
    id: 'ca-1', domain: 'calendar',
    title: 'Team standup',
    date: '2026-06-04T08:00:00Z', syncedAt: FRESH_SYNC,
    externalUrl: 'https://calendar.google.com/calendar/event?eid=1aB',
    cells: { date: 'Thu 4 Jun 09:00', location: 'Google Meet', attendees: '4', calendar: 'Work' },
    detail: [
      { label: 'Event', value: 'Team standup' },
      { label: 'Start', value: 'Thu 4 Jun 2026, 09:00' },
      { label: 'End', value: 'Thu 4 Jun 2026, 09:15' },
      { label: 'Recurrence', value: 'Every weekday' },
      { label: 'Location', value: 'Google Meet' },
      { label: 'Attendees', value: 'Matt, Tom, Lisa, Sarah' },
      { label: 'Calendar', value: 'Work' },
      { label: 'Status', value: 'Confirmed' },
    ],
    body: 'Daily standup. Format: yesterday / today / blockers. 15 minutes hard stop.',
    meta: { Calendar: ['Work'], When: ['Today', 'This week'] },
  },
  {
    id: 'ca-2', domain: 'calendar',
    title: 'Q2 budget review',
    date: '2026-06-04T10:00:00Z', syncedAt: FRESH_SYNC,
    externalUrl: 'https://calendar.google.com/calendar/event?eid=2cD',
    cells: { date: 'Thu 4 Jun 11:00', location: 'Meeting Room 2', attendees: '3', calendar: 'Work' },
    detail: [
      { label: 'Event', value: 'Q2 budget review' },
      { label: 'Start', value: 'Thu 4 Jun 2026, 11:00' },
      { label: 'End', value: 'Thu 4 Jun 2026, 11:45' },
      { label: 'Location', value: 'Meeting Room 2' },
      { label: 'Attendees', value: 'Matt, Sarah, James Wright' },
      { label: 'Calendar', value: 'Work' },
      { label: 'Status', value: 'Confirmed' },
    ],
    body: 'Agenda: infrastructure spend, Acme contract renewal, TMA clinical team headcount. See "Q2 budget review — agenda attached" email.',
    meta: { Calendar: ['Work'], When: ['Today', 'This week'] },
  },
  {
    id: 'ca-3', domain: 'calendar',
    title: 'School pickup',
    date: '2026-06-04T14:30:00Z', syncedAt: FRESH_SYNC,
    externalUrl: 'https://calendar.google.com/calendar/event?eid=3eF',
    cells: { date: 'Thu 4 Jun 15:30', location: '—', attendees: '1', calendar: 'Personal' },
    detail: [
      { label: 'Event', value: 'School pickup' },
      { label: 'Start', value: 'Thu 4 Jun 2026, 15:30' },
      { label: 'End', value: 'Thu 4 Jun 2026, 16:00' },
      { label: 'Calendar', value: 'Personal' },
      { label: 'Status', value: 'Confirmed' },
    ],
    meta: { Calendar: ['Personal'], When: ['Today', 'This week'] },
  },
  {
    id: 'ca-4', domain: 'calendar',
    title: 'TMA clinical review',
    date: '2026-06-05T09:00:00Z', syncedAt: FRESH_SYNC,
    externalUrl: 'https://calendar.google.com/calendar/event?eid=4gH',
    cells: { date: 'Fri 5 Jun 10:00', location: 'Google Meet', attendees: '5', calendar: 'Work' },
    detail: [
      { label: 'Event', value: 'TMA clinical review' },
      { label: 'Start', value: 'Fri 5 Jun 2026, 10:00' },
      { label: 'End', value: 'Fri 5 Jun 2026, 11:00' },
      { label: 'Location', value: 'Google Meet' },
      { label: 'Attendees', value: 'Matt, Sarah, + 3 clinicians' },
      { label: 'Calendar', value: 'Work' },
      { label: 'Status', value: 'Confirmed' },
    ],
    body: 'Monthly clinical safety review: hazard log walkthrough, incident review, DCB0129 compliance check.',
    meta: { Calendar: ['Work'], When: ['This week'] },
  },
  {
    id: 'ca-5', domain: 'calendar',
    title: 'Acme Corp partnership call',
    date: '2026-06-05T13:00:00Z', syncedAt: FRESH_SYNC,
    externalUrl: 'https://calendar.google.com/calendar/event?eid=5iJ',
    cells: { date: 'Fri 5 Jun 14:00', location: 'Zoom', attendees: '2', calendar: 'Work' },
    detail: [
      { label: 'Event', value: 'Acme Corp partnership call' },
      { label: 'Start', value: 'Fri 5 Jun 2026, 14:00' },
      { label: 'End', value: 'Fri 5 Jun 2026, 14:30' },
      { label: 'Location', value: 'Zoom' },
      { label: 'Attendees', value: 'Matt, James Wright (Acme)' },
      { label: 'Calendar', value: 'Work' },
      { label: 'Status', value: 'Confirmed' },
    ],
    body: 'Review contract section 4.2 (data residency). Acme wants EU-hosted deployment.',
    meta: { Calendar: ['Work'], When: ['This week'] },
  },
  {
    id: 'ca-6', domain: 'calendar',
    title: 'Dentist',
    date: '2026-06-06T07:30:00Z', syncedAt: FRESH_SYNC,
    externalUrl: 'https://calendar.google.com/calendar/event?eid=6kL',
    cells: { date: 'Sat 6 Jun 08:30', location: '—', attendees: '1', calendar: 'Personal' },
    detail: [
      { label: 'Event', value: 'Dentist' },
      { label: 'Start', value: 'Sat 6 Jun 2026, 08:30' },
      { label: 'End', value: 'Sat 6 Jun 2026, 09:30' },
      { label: 'Calendar', value: 'Personal' },
      { label: 'Status', value: 'Confirmed' },
    ],
    meta: { Calendar: ['Personal'], When: ['This week'] },
  },
  {
    id: 'ca-7', domain: 'calendar',
    title: 'Sprint 23 planning',
    date: '2026-06-09T09:00:00Z', syncedAt: FRESH_SYNC,
    externalUrl: 'https://calendar.google.com/calendar/event?eid=7mN',
    cells: { date: 'Tue 9 Jun 10:00', location: 'Google Meet', attendees: '4', calendar: 'Work' },
    detail: [
      { label: 'Event', value: 'Sprint 23 planning' },
      { label: 'Start', value: 'Tue 9 Jun 2026, 10:00' },
      { label: 'End', value: 'Tue 9 Jun 2026, 11:30' },
      { label: 'Location', value: 'Google Meet' },
      { label: 'Attendees', value: 'Matt, Tom, Lisa, Sarah' },
      { label: 'Calendar', value: 'Work' },
      { label: 'Status', value: 'Confirmed' },
    ],
    body: 'Sprint 23 scope: Data page tables (ZEN-156), zombie query guard (ZEN-151), Gmail body split (ZEN-149).',
    meta: { Calendar: ['Work'], When: ['Upcoming'] },
  },
  {
    id: 'ca-8', domain: 'calendar',
    title: '1:1 with Tom',
    date: '2026-06-09T14:00:00Z', syncedAt: FRESH_SYNC,
    externalUrl: 'https://calendar.google.com/calendar/event?eid=8oP',
    cells: { date: 'Tue 9 Jun 15:00', location: '—', attendees: '2', calendar: 'Work' },
    detail: [
      { label: 'Event', value: '1:1 with Tom' },
      { label: 'Start', value: 'Tue 9 Jun 2026, 15:00' },
      { label: 'End', value: 'Tue 9 Jun 2026, 15:30' },
      { label: 'Attendees', value: 'Matt, Tom' },
      { label: 'Calendar', value: 'Work' },
      { label: 'Status', value: 'Confirmed' },
    ],
    meta: { Calendar: ['Work'], When: ['Upcoming'] },
  },
]

export const DOMAIN_RECORDS: Record<DomainKey, DataRecord[]> = {
  email: EMAIL_RECORDS,
  drive: DRIVE_RECORDS,
  confluence: CONFLUENCE_RECORDS,
  jira: JIRA_RECORDS,
  calendar: CALENDAR_RECORDS,
}

// Semantic search mock results ----------------------------------------
// Confluence + Drive only. Keyed by a few demo queries; any other query
// returns a generic ranked subset so the interaction always demonstrates.

export function semanticResults(domain: DomainKey, query: string): SemanticHit[] {
  const records = DOMAIN_RECORDS[domain]
  if (domain === 'confluence') {
    return [
      { record: records[0], relevance: 0.92, excerpt: '…introduce a token-bucket limiter keyed on (workspace_id, endpoint) with a default of 60 req/min and a burst of 20…' },
      { record: records[5], relevance: 0.81, excerpt: '…phantom worker registration identified… stale worker registrations after an unclean container restart…' },
      { record: records[1], relevance: 0.74, excerpt: '…three planes: voice, text chat, and async tasks. All data flows through Convex…' },
      { record: records[4], relevance: 0.62, excerpt: '…certbot renew --force-renewal… reload Caddy via coolify cli redeploy…' },
    ]
  }
  return [
    { record: records[4], relevance: 0.89, excerpt: '…DCB0129 clinical safety case… hazard log, clinical risk management plan, safety case report…' },
    { record: records[0], relevance: 0.77, excerpt: '…data plane completeness — every synced source browsable + searchable… desktop app (Electrobun)…' },
    { record: records[2], relevance: 0.68, excerpt: '…contract section 4.2 (data residency) needs legal review… Acme wants EU-hosted deployment…' },
  ]
}
